<script lang="ts">
    import { DEFAULT_BOX, DEFAULT_LAYOUT } from '$lib/components/constants';

    import { getMediaById } from '$lib/components/Blocks/_utils/getMediaById';
    import Transition from '$lib/components/Blocks/_wrappers/Transition.svelte';
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { cn } from '$lib/utils/cn';

    import { TEXT_SIZE_MAP } from './constants';
    import { getListItemVisualSizeProps } from './helper';
    import Emoji from '../_wrappers/Emoji.svelte';
    import Icon from '../Icon/Icon.View.svelte';
    import Image from '../Image/Image.View.svelte';
    import Text from '../Text/Text.View.svelte';

    import type {
        Align,
        BorderRadius,
        VisualSize,
        Layout,
        TextSize,
    } from '$lib/types/Components/SharedTypes';
    import type { TransitionEffect } from '$lib/types/Theme';
    import type { JSONContent } from '@tiptap/core';

    export let align: Align = 'left';
    export let textSize: TextSize = 'small';
    export let visualSize: VisualSize = 'small';
    export let color = 'black';
    export let iconColor = 'black';
    export let draft: JSONContent;
    export let wysiwygHtml: string | undefined = undefined;
    export let emoji = ':telescope:';
    export let imageSrc = '';
    export let mediaType = 'emoji';
    export let cornerRounding: BorderRadius = 'small';
    export let linkColor = '';
    export let svg = '';
    export let iconName = '';
    export let platform = '';
    export let isAnimated = false;
    export let jsonUrl: string | null = null;
    export let transitionEffect: TransitionEffect = 'default';
    export let transitionDelay = 0;
    export let mediaIds: string[] = [];

    let visualProps: {
            size: number;
            emojiPadding: number;
        },
        emojiSize: number;
    let visualClasses: string, imageClasses: string;

    const media = getMediaById(mediaIds.length > 0 ? mediaIds[0] : '');

    const alignCenter = align === 'center';
    const cornerRoundingMajor = cornerRounding === 'large';

    visualProps = getListItemVisualSizeProps({
        align,
        visualSize,
        srcType: mediaType,
    });
    emojiSize = visualProps.size && visualProps.size - visualProps.emojiPadding * 2;

    const iconLayout: Layout = {
        ...DEFAULT_LAYOUT,
        isInList: true,
    };

    visualClasses = 'fun-flex-shrink-0 fun-flex fun-items-center fun-justify-center';

    $: imageClasses = cn(visualClasses, {
        ['fun-overflow-hidden fun-rounded-full']: cornerRoundingMajor,
    });

    const textTopMarginClass = visualSize === 'xSmall' ? 'fun-mt-2' : 'fun-mt-3';
    const textLeftMarginClass = visualSize === 'xSmall' ? 'fun-ml-4' : 'fun-ml-6';
    const textMarginClass = alignCenter ? textTopMarginClass : textLeftMarginClass;

    const textClasses = cn(
        'fun-text fun-flex fun-w-full fun-flex-grow fun-items-center fun-break-words',
        { 'fun-min-w-0': !alignCenter },
        textMarginClass,
    );
</script>

<Transition delay={transitionDelay} effect={transitionEffect}>
    <div
        class="fun-box-border fun-flex fun-w-full fun-align-top {alignCenter
            ? 'fun-flex-col fun-items-center'
            : 'fun-items-start'}"
    >
        {#if mediaType === 'emoji'}
            <div
                class={visualClasses}
                style={`width: ${visualProps?.size}px; height: ${visualProps?.size}px;`}
            >
                <Emoji {emoji} size={emojiSize ? `${emojiSize}px` : undefined} />
            </div>
        {/if}
        {#if mediaType === 'image'}
            <div
                class={imageClasses}
                style={`transform: translateZ(0); width: ${visualProps?.size}px; height: ${visualProps?.size}px;`}
            >
                <Image
                    src={imageSrc}
                    size="listItem"
                    fixedMaxWidth={visualProps?.size}
                    borderRadiusClass={getBorderRadius('listImage')}
                    transitionEffect="none"
                    alt={media?.alt || media?.altDescription}
                />
            </div>
        {/if}
        {#if mediaType === 'icon'}
            <div
                class={visualClasses}
                style={`transform: translateZ(0); width: ${visualProps?.size}px; height: ${visualProps?.size}px;`}
            >
                <Icon
                    {svg}
                    {iconName}
                    box={DEFAULT_BOX}
                    layout={iconLayout}
                    color={iconColor}
                    {isAnimated}
                    {jsonUrl}
                    {platform}
                    transitionEffect="none"
                />
            </div>
        {/if}
        <div
            class={textClasses}
            style={`min-height: ${alignCenter ? 0 : visualProps?.size}px; font-size: ${
                TEXT_SIZE_MAP[textSize]
            }`}
        >
            <Text
                raw={draft}
                {wysiwygHtml}
                box={DEFAULT_BOX}
                inheritSize
                {color}
                {align}
                transitionEffect="none"
            />
        </div>
    </div>
</Transition>

<style lang="scss">
    .text > :global(div) {
        @apply fun-h-full fun-w-full;
    }

    .text :global(h4),
    .text :global(p) {
        padding: 0 !important;
    }
</style>
